import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../context/AuthContext';

interface Template {
  id: number;
  templateName: string | null;
  language: string | null;
  formName: string | null;
  caregiverType: string | null;
  createdAt: string;
  updatedAt: string;
}

type SortColumn = 'templateName' | 'formName' | 'language' | 'caregiverType' | 'createdAt';
type SortDirection = 'asc' | 'desc';

export default function TemplateList() {
  const { authFetch } = useAuth();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Upload form state
  const [templateName, setTemplateName] = useState('');
  const [language, setLanguage] = useState('');
  const [formName, setFormName] = useState('');
  const [caregiverType, setCaregiverType] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  // Sort state
  const [sortColumn, setSortColumn] = useState<SortColumn>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Column filters
  const [filters, setFilters] = useState({
    templateName: '',
    formName: '',
    language: '',
    caregiverType: '',
    createdAt: '',
  });

  // Inline editing
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Record<string, string>>({});

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await authFetch('/api/templates');
      if (res.ok) setTemplates(await res.json());
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleUpload = async () => {
    if (!templateName || !pdfFile) {
      alert('Please enter a template name and select a PDF file.');
      return;
    }
    const formData = new FormData();
    formData.append('templateName', templateName);
    formData.append('pdfFile', pdfFile);
    formData.append('language', language);
    formData.append('formName', formName);
    formData.append('caregiverType', caregiverType);

    try {
      await authFetch('/api/templates/upload', {
        method: 'POST',
        body: formData,
      });
      setShowUploadModal(false);
      setTemplateName('');
      setPdfFile(null);
      setLanguage('');
      setFormName('');
      setCaregiverType('');
      fetchTemplates();
    } catch (err) {
      console.error('Error uploading template:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    try {
      await authFetch(`/api/templates/${id}`, { method: 'DELETE' });
      fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
    }
  };

  const startEditing = (t: Template) => {
    setEditingId(t.id);
    setEditData({
      templateName: t.templateName || '',
      formName: t.formName || '',
      language: t.language || '',
      caregiverType: t.caregiverType || '',
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEditing = async () => {
    if (editingId === null) return;
    try {
      await authFetch(`/api/templates/${editingId}/fields`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData),
      });
      setTemplates((prev) =>
        prev.map((t) => (t.id === editingId ? { ...t, ...editData } : t))
      );
      setEditingId(null);
      setEditData({});
    } catch (err) {
      console.error('Error updating template:', err);
    }
  };

  const handleFilterChange = (column: string, value: string) => {
    setFilters((prev) => ({ ...prev, [column]: value }));
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const filteredTemplates = templates
    .filter((t) => {
      const match = (val: string | null, filter: string) =>
        !filter || (val || '').toLowerCase().includes(filter.toLowerCase());
      return (
        match(t.templateName, filters.templateName) &&
        match(t.formName, filters.formName) &&
        match(t.language, filters.language) &&
        match(t.caregiverType, filters.caregiverType) &&
        match(formatDate(t.createdAt), filters.createdAt)
      );
    })
    .sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      if (sortColumn === 'createdAt') {
        aVal = new Date(a.createdAt).getTime();
        bVal = new Date(b.createdAt).getTime();
      } else {
        aVal = (a[sortColumn] || '').toLowerCase();
        bVal = (b[sortColumn] || '').toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const columns: { key: SortColumn; label: string; minWidth: number }[] = [
    { key: 'templateName', label: 'Template Name', minWidth: 180 },
    { key: 'formName', label: 'Form Name', minWidth: 150 },
    { key: 'language', label: 'Language', minWidth: 120 },
    { key: 'caregiverType', label: 'Caregiver Type', minWidth: 130 },
    { key: 'createdAt', label: 'Created Date', minWidth: 140 },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Template Management</Typography>
        <Button variant="contained" onClick={() => setShowUploadModal(true)}>
          Add New Template
        </Button>
      </Box>

      {/* Upload dialog */}
      <Dialog open={showUploadModal} onClose={() => setShowUploadModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload New Template</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Template Name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} sx={{ mt: 1 }} />
          <TextField fullWidth label="Language" value={language} onChange={(e) => setLanguage(e.target.value)} sx={{ mt: 2 }} />
          <TextField fullWidth label="Form Name" value={formName} onChange={(e) => setFormName(e.target.value)} sx={{ mt: 2 }} />
          <TextField fullWidth label="Caregiver Type" value={caregiverType} onChange={(e) => setCaregiverType(e.target.value)} sx={{ mt: 2 }} />
          <input style={{ marginTop: 16, display: 'block' }} type="file" accept=".pdf" onChange={(e) => setPdfFile(e.target.files?.[0] || null)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload}>Upload</Button>
        </DialogActions>
      </Dialog>

      {/* Clear filters button */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            setFilters({ templateName: '', formName: '', language: '', caregiverType: '', createdAt: '' })
          }
        >
          Clear Filters
        </Button>
      </Box>

      {/* Table */}
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ minWidth: col.minWidth, fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === col.key}
                    direction={sortColumn === col.key ? sortDirection : 'asc'}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell sx={{ minWidth: 140, fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
            {/* Filter row */}
            <TableRow>
              {columns.map((col) => (
                <TableCell key={`filter-${col.key}`} sx={{ py: 0.5 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Filter..."
                    value={filters[col.key as keyof typeof filters] || ''}
                    onChange={(e) => handleFilterChange(col.key, e.target.value)}
                    fullWidth
                  />
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTemplates.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No templates found
                </TableCell>
              </TableRow>
            )}
            {filteredTemplates.map((t) => (
              <TableRow key={t.id} hover>
                {editingId === t.id ? (
                  <>
                    <TableCell>
                      <TextField size="small" value={editData.templateName} onChange={(e) => setEditData({ ...editData, templateName: e.target.value })} fullWidth />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={editData.formName} onChange={(e) => setEditData({ ...editData, formName: e.target.value })} fullWidth />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={editData.language} onChange={(e) => setEditData({ ...editData, language: e.target.value })} fullWidth />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={editData.caregiverType} onChange={(e) => setEditData({ ...editData, caregiverType: e.target.value })} fullWidth />
                    </TableCell>
                    <TableCell>{formatDate(t.createdAt)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Save">
                          <IconButton size="small" color="success" onClick={saveEditing}><SaveIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={cancelEditing}><CloseIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{t.templateName || 'N/A'}</TableCell>
                    <TableCell>{t.formName || 'N/A'}</TableCell>
                    <TableCell>{t.language || 'N/A'}</TableCell>
                    <TableCell>{t.caregiverType || 'N/A'}</TableCell>
                    <TableCell>{formatDate(t.createdAt)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" color="warning" onClick={() => startEditing(t)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="View">
                          <IconButton size="small" color="info" onClick={() => navigate(`/templates/${t.id}`)}><VisibilityIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(t.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      </Paper>
    </Box>
  );
}
